import {
  closeCurrentDialog,
  FormSubmitHandler,
  openDialog,
  showNotification,
  useLocalStorage,
} from 'platform/components';

import {useCallback, useEffect, useRef, useState} from 'react';

import {equals, isEmpty, isNil, isNotNil} from 'ramda';

import {
  SourcingPresetResponseBody,
  useLazySourcingGetPresetQuery,
  useSourcingCreatePresetMutation,
  useSourcingGetPresetsListQuery,
  useSourcingUpdatePresetMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, pushGtmEvent, useOnMount, useQueryState} from 'shared/web';

import {sourcingStorageKey} from '../../../constants/sourcingStorageKey';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {useSourcingType} from '../../../hooks/useSourcingType';
import {
  FilterPresetForm,
  SourcingFilterPresetFormData,
} from '../components/FilterPresetForm/FilterPresetForm';
import {useFilters} from '../components/FiltersContext/FiltersContext';
import {DEFAULT_FILTERS, FiltersType} from '../types/FiltersType';
import {convertFiltersBodyToFiltersType} from '../utils/convertFiltersBodyToFiltersType';
import {convertFiltersTypeToFiltersBody} from '../utils/convertFiltersTypeToFiltersBody';
import {readAuctionTypeFilter} from '../utils/readAuctionTypeFilter';

type SourcingFilterPreset = Omit<SourcingPresetResponseBody, 'filters'> & {
  filters: FiltersType;
};

export type SourcingFilterPresetsControl = {
  /** Optimistic preset id for button highlight. Flips on click, before filters load. */
  selectedFilterPresetId: string | Nullish;
  /** Settled preset id that is safe to feed into the request body. Flips together with the
   * filters once the preset load resolves, so the request body never contains a hybrid of
   * the new preset id + the previous preset's filters (which would create a junk cache key
   * and trigger a one-page fetch before the real cache hit). */
  appliedFilterPresetId: string | Nullish;
  onSelectFilterPresetId: (id: string) => void;
  /** Opens the create dialog for the current filters. With a preset selected, the dialog is
   * prefilled from it, so the modified filters end up as a new preset and the selected one is
   * left intact (unlike onUpdateSelectedFilterPreset). */
  onCreateFilterPreset: () => void;
  onUpdateSelectedFilterPreset: () => void;
  onResetFilterPreset: () => void;
  selectedFilterPreset: SourcingFilterPreset | Nullish;
  isOutdated: boolean;
  isEmpty: boolean;
  isLoading: boolean;
};

const useInitialFilterPresetId = () =>
  useLocalStorage<string | null>(sourcingStorageKey.FILTER_PRESET_ID, null);

export function useSourcingFilterPresets(filterModalOpened: boolean): SourcingFilterPresetsControl {
  const {filters, onChange} = useFilters();
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const sourcingType = useSourcingType();
  const [queryFilterPresetId, , clearQueryFilterPresetId] = useQueryState('filterPresetId');
  // selectedFilterPresetId is for fast interaction after click on button
  const [selectedFilterPresetId, setSelectedFilterPresetId] = useState<string | Nullish>();

  // appliedFilterPresetId only flips once filters from the preset have actually loaded, so the
  // request body and cache key never see a (newPresetId + oldFilters) hybrid mid-transition.
  const [appliedFilterPresetId, setAppliedFilterPresetId] = useState<string | Nullish>();

  // selectedFilterPreset is filter preset loaded from api after click on button
  const [selectedFilterPreset, setSelectedFilterPreset] = useState<
    SourcingFilterPreset | Nullish
  >();

  const [savedFilterPresetId, saveFilterPresetId] = useInitialFilterPresetId();
  const handledQueryFilterPresetIdRef = useRef<string | null>(null);

  const {data: presetsList} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });

  const [loadPreset, {isFetching}] = useLazySourcingGetPresetQuery();

  const resetFilterPreset = useCallback(() => {
    setSelectedFilterPresetId(null);
    setAppliedFilterPresetId(null);
    setSelectedFilterPreset(null);
    saveFilterPresetId(null);
  }, [saveFilterPresetId]);

  const loadFilterPreset = useCallback(
    (
      presetId: string,
      options?: {saveToLocalStorage?: boolean; clearQuery?: boolean; trackApplication?: boolean}
    ) => {
      setSelectedFilterPresetId(presetId);
      // preferCacheValue: skip the server roundtrip on A→B→A switches; tag-based invalidation
      // on update/duplicate keeps the cache from going stale.
      loadPreset({id: presetId}, true)
        .unwrap()
        .then((filterPreset) => {
          // Flip filters and the cache-key preset id together in one render so the request
          // body transitions straight from old-preset to new-preset, never through a hybrid.
          setSelectedFilterPreset({
            ...filterPreset,
            filters: convertFiltersBodyToFiltersType(filterPreset.filters),
          });
          onChange(convertFiltersBodyToFiltersType(filterPreset.filters));
          // The backend otherwise restores the obsolete filter from the stored preset.
          setAppliedFilterPresetId(
            readAuctionTypeFilter(filterPreset.filters).hasUnsupportedValues ? null : presetId
          );
          if (options?.trackApplication) {
            pushGtmEvent({
              event: `sourcing_${sourcingType}_preset_applied`,
              event_id: `sourcing_${sourcingType}_preset_applied`,
            });
          }
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(filterPreset.id);
          }
        })
        .catch(() => {
          setSelectedFilterPresetId(null);
          setAppliedFilterPresetId(null);
          setSelectedFilterPreset(null);
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(null);
          }
        })
        .finally(() => {
          if (options?.clearQuery) {
            clearQueryFilterPresetId();
          }
        });
    },
    [loadPreset, onChange, saveFilterPresetId, clearQueryFilterPresetId, sourcingType]
  );

  // Load filter preset from local storage on mount (only if no query parameter)
  useOnMount(() => {
    if (!queryFilterPresetId && savedFilterPresetId) {
      // Saving re-stores the same id on success and clears it on failure, so a preset that no longer
      // loads (deleted, or saved by another user before logout) is not requested on every mount.
      loadFilterPreset(savedFilterPresetId, {saveToLocalStorage: true});
    }
  });

  // Load filter preset when query parameter changes (or on mount if query parameter exists).
  // loadFilterPreset changes identity on every render (clearQueryFilterPresetId from useQueryState
  // is unstable), so each query value must be handled once, otherwise a cached preset re-triggers
  // this effect in a synchronous loop (Sentry TEAS-FE-7WG).
  useEffect(
    function loadFilterPresetFromQuery() {
      if (isNil(queryFilterPresetId) || isEmpty(queryFilterPresetId)) {
        handledQueryFilterPresetIdRef.current = null;
        return;
      }
      if (handledQueryFilterPresetIdRef.current === queryFilterPresetId) {
        return;
      }
      handledQueryFilterPresetIdRef.current = queryFilterPresetId;
      loadFilterPreset(queryFilterPresetId, {saveToLocalStorage: true, clearQuery: true});
    },
    [queryFilterPresetId, loadFilterPreset]
  );

  const isOutdated =
    isNotNil(selectedFilterPreset) &&
    !equals(filters, {
      ...DEFAULT_FILTERS,
      ...selectedFilterPreset?.filters,
    });

  /* Unselect filter preset when:
   - selected filters !== filters from filter preset
   - and (filters change when filter modal is closed
          - or on filter modal close) */
  useEffect(
    function resetOutdatedFilterPreset() {
      if (!filterModalOpened && isOutdated) {
        resetFilterPreset();
      }
    },
    [filterModalOpened, isOutdated, resetFilterPreset]
  );

  const [createPreset] = useSourcingCreatePresetMutation();
  const [updatePreset] = useSourcingUpdatePresetMutation();

  const onSelectFilterPresetId = (filterPresetId: string) => {
    if (filterPresetId === selectedFilterPresetId) {
      pushGtmEvent({
        event: `sourcing_${sourcingType}_preset_unselected`,
        event_id: `sourcing_${sourcingType}_preset_unselected`,
      });
      resetFilterPreset();
      return;
    }

    loadFilterPreset(filterPresetId, {saveToLocalStorage: true, trackApplication: true});
  };

  const openCreateFilterPresetDialog = (initialValues: SourcingFilterPresetFormData) => {
    const onSubmit: FormSubmitHandler<SourcingFilterPresetFormData> = (values) =>
      createPreset({
        createPresetRequestBody: {
          name: values.name,
          allowNotifications: values.allowNotifications ?? false,
          filters: convertFiltersTypeToFiltersBody(filters),
        },
      })
        .unwrap()
        .then((createdFilterPreset) => {
          pushGtmEvent({
            event: `sourcing_${sourcingType}_preset_created`,
            event_id: `sourcing_${sourcingType}_preset_created`,
          });
          closeCurrentDialog();
          onSelectFilterPresetId(createdFilterPreset.id);
        })
        .catch(handleApiError);

    openDialog(<FilterPresetForm initialValues={initialValues} onSubmit={onSubmit} />, {
      title: i18n.t('entity.sourcing.filterPresets.labels.create'),
    });
  };

  const onCreateFilterPreset = () =>
    openCreateFilterPresetDialog({
      name: selectedFilterPreset?.name ?? '',
      allowNotifications: selectedFilterPreset?.allowNotifications ?? true,
    });

  const onUpdateSelectedFilterPreset = () => {
    if (!selectedFilterPresetId) {
      return;
    }

    return updatePreset({
      id: selectedFilterPresetId,
      updatePresetRequestBody: {
        filters: convertFiltersTypeToFiltersBody(filters),
      },
    })
      .unwrap()
      .then(() => {
        pushGtmEvent({
          event: `sourcing_${sourcingType}_preset_updated`,
          event_id: `sourcing_${sourcingType}_preset_updated`,
        });
        setSelectedFilterPreset((prev) => (isNotNil(prev) ? {...prev, filters} : prev));
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.success'));
      })
      .catch(handleApiError);
  };

  return {
    selectedFilterPresetId,
    appliedFilterPresetId,
    onSelectFilterPresetId,
    onCreateFilterPreset,
    onUpdateSelectedFilterPreset,
    onResetFilterPreset: resetFilterPreset,
    selectedFilterPreset,
    isOutdated,
    isEmpty: (presetsList?.length ?? 0) === 0,
    isLoading: isFetching,
  };
}
